import { memo, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { transformRoute, useTranslate } from "helpers";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { initialFleetVehicleSearchForm } from "services/api/fleet/vehicles/consts";
import { getFleetVehiclesAPI } from "services/api/fleet/vehicles/api";
import {
  FleetVehicleRow,
  FleetVehicleSearchFormValues,
} from "services/api/fleet/vehicles/types";
import { NoResults } from "ui/components/NoResults";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Iste kolone/prikaz kao na /fleet/vehicle/search (bez filtera i view gumba na dashboardu).
import VehicleSearchResults from "ui/modules/fleet/vehicles/pages/VehicleSearchPage/components/VehicleSearchResults/VehicleSearchResults";
import { useSelectedStringItemsChanges } from "ui/modules/fleet/vehicles/pages/VehicleSearchPage/hooks";

import { VehicleSearchTabProps } from "./types";

const VehicleSearchTab: React.FC<VehicleSearchTabProps> = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleRow>
  >(initialDataWithPagination);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentFilters] = useState<FleetVehicleSearchFormValues>(
    initialFleetVehicleSearchForm
  );
  const searchRequestIdRef = useRef(0);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    filters: FleetVehicleSearchFormValues = currentFilters
  ) => {
    const requestId = ++searchRequestIdRef.current;
    setIsLoading(true);
    try {
      const results = await getFleetVehiclesAPI(
        pagination,
        filters.search,
        filters.vehicleManufacturerId,
        filters.vehicleModelId,
        filters.vehicleTypeId,
        null,
        null,
        null,
        {
          vinNumber: filters.vinNumber,
          firstRegistrationDateFrom: filters.firstRegistrationDateFrom,
          firstRegistrationDateTo: filters.firstRegistrationDateTo,
          registrationPlate: filters.registrationPlate,
          registrationExpirationFrom: filters.registrationExpirationFrom,
          registrationExpirationTo: filters.registrationExpirationTo,
          kwFrom: filters.kwFrom,
          kwTo: filters.kwTo,
          kmTraveledFrom: filters.kmTraveledFrom,
          kmTraveledTo: filters.kmTraveledTo,
        }
      );

      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(results);
    } catch {
      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    if (requestId !== searchRequestIdRef.current) return;
    setIsLoading(false);
  };

  const handleRowClick = (id: string) => {
    navigate(
      transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, { id })
    );
  };

  const handleSelectedChange = useSelectedStringItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <div className="pt-2">
      {!isLoading && searchResults.pagination.totalPages === 0 ? (
        <NoResults message={t("admin.vehicle.search.noresults")} />
      ) : (
        <VehicleSearchResults
          searchResults={searchResults}
          selectedRows={selectedRows}
          isLoading={isLoading}
          selectableRows={true}
          viewType="table"
          onRowClick={handleRowClick}
          onPaginationChange={handlePaginationChange}
          onSelectedChange={handleSelectedChange}
        />
      )}
    </div>
  );
};

export default memo(VehicleSearchTab);
